/** @format */
import { h } from 'vue';
import { type RouteRecordRaw } from 'vue-router';
import { useGlobalStoreWithOut, getFirstWordFromLocationPathname, getEnvUrlByUrl } from '@power/runtime-vue3';
import { isExternal, loadView, flatArr } from '@power/utils';
import { matchedCommonPathComponent } from './utils';
import type { MenuItem as Menu } from '@power/runtime-vue3';
import Error from '@/router/components/Error.vue';
import { PcNotFound } from '@/components/pc-notFound/index';

import { RouterView } from '@/layout/parent-layout/index';
import InnerLink from '@/p-layout/components/inner-link/index.vue';
import { useRouterStoreWithOut } from '@/store/modules/router';
import { menuUtil } from '@/utils/menu';

/**
 * 动态生成路由
 * @returns {Promise<Router>}
 */

/**
 * 优先加载配置的动态路由
 * 其次加载views里面对应的vue文件
 * 最后加载动态参数的动态路由 /aaa/bbb/:id/:name
 * @param file 路径 path
 */
export const _import = (file) => {
	const routerStore = useRouterStoreWithOut();
	let Component = routerStore.constantRouterComponents[file] || loadView(file, routerStore.vueModules);
	if (Component) {
		return Component;
	} else {
		Component = matchedCommonPathComponent(file)?.Component;
		return Component;
	}
};

export function filterAsyncRoute(routes: Menu[]): RouteRecordRaw[] {
	const globalStore = useGlobalStoreWithOut();
	const curSystem = globalStore.curSystem;
	const curSystemAlias = globalStore.curSystem?.alias;
	// console.log('curSystemAlias', curSystemAlias);
	return routes
		.map((item) => {
			const { path, name, icon, orderNum, keepAlive, perms, sysType, routerParam, type, isShow, alias, desc } = item;
			let newPath = getEnvUrlByUrl(path);
			let link = '';
			const firstWord = getFirstWordFromLocationPathname(path);
			// console.log('path', path);
			// console.log('firstWord', firstWord);

			const isHttp = isExternal(path);
			if (type === 1) {
				if ('outer' === sysType) {
					// outer
					if (isHttp) {
						newPath = path;
					} else {
						newPath = `${window.location.origin}${newPath}`;
					}
				} else if ('inner' === sysType) {
					// inner
					link = encodeURIComponent(newPath);
					if (curSystem.isLocal) {
						newPath = `/inner/${link}`;
					} else {
						newPath = `/${curSystemAlias}/inner/${link}`;
					}
				} else if ('self' === sysType) {
					// self
					if (curSystemAlias === firstWord) {
						newPath = path.substring(curSystemAlias.length + 1);
						// console.log('newPath /**', newPath);
					} else {
						// newPath = `${newPath}`;
					}
				}
			}

			const route: any = {
				path: newPath,
				name: alias,
				meta: {
					isHttp,
					link,
					orderNum,
					title: name,
					type: item.type,
					sysType,
					routerParam,
					perms,
					icon,
					keepAlive,
					isShow,
					desc,
				},
			};

			if (!isShow) {
				return undefined;
			}

			if (type === 0) {
				// 如果是目录
				const children = item.children ? filterAsyncRoute(item.children) : [];
				if (children?.length) {
					route.component = RouterView;
					route.children = children;
					route.redirect = { name: children[0].name };
				} else {
					route.component = h(Error, { name });
				}
				return route;
			} else if (type === 1) {
				// 如果是页面
				let Component = PcNotFound;
				if ('outer' === sysType) {
					Component = PcNotFound;
				} else if ('inner' === sysType) {
					Component = InnerLink;
				} else if ('self' === sysType) {
					route.path = newPath.split('?')[0];
					route.fullPath = newPath;
					// self
					const matchedObj = matchedCommonPathComponent(newPath);
					if (matchedObj) {
						route.path = matchedObj.path;
						route.fullPath = newPath;
						route.meta.activeMenu = newPath;
						route.component = matchedObj.Component;
						return route;
					} else {
						Component = _import(newPath) || PcNotFound;
					}
				}
				route.component = Component;
				// route.component = h(Error, { name });

				const perms = routes.filter((n) => n.parentId === item.id).flatMap((n) => n.perms?.split(','));
				if (route.meta && perms) {
					// 设置当前页面所拥有的权限
					route.meta.perms = perms;
				}
				return route;
			}
			return undefined;
		})
		.filter((item): item is RouteRecordRaw => !!item);
}

export const generatorDynamicRouter = (asyncMenus) => {
	try {
		const routerStore = useRouterStoreWithOut();
		const { routes, router, common, endRoutes } = routerStore;
		const routeList = filterAsyncRoute(asyncMenus);
		const layout = routes.find((item) => item.name == 'Layout');
		const outsideLayout = routes.filter((item) => item.name !== 'Layout');

		const mergedArray = mergeCommonRoutes(common, routeList);
		const menus = [...mergedArray, ...endRoutes];

		menuUtil.setFullPath(menus);
		layout.children = menus;
		const removeRoute = router.addRoute(layout);
		// 获取所有没有包含children的路由，上面addRoute的时候，vue-router已经帮我们拍平了所有路由
		const filterRoutes = router
			.getRoutes()
			.filter(
				(item) =>
					(!item.children.length || Object.is(item.meta?.hideChildrenInMenu, true)) &&
					!outsideLayout.some((n) => n.name === item.name),
			);
		// 清空所有路由
		removeRoute();
		layout.children = [...filterRoutes];
		// 重新添加拍平后的路由
		router.addRoute(layout);
		//console.log('所有路由', router.getRoutes());

		return {
			menus,
			routes: layout.children,
		};
	} catch (error) {
		console.error('生成路由时出错', error);
		return {
			menus: [],
			routes: [],
		};
	}
};

// 过滤静态路由
function mergeCommonRoutes(common, routeList) {
	const flatCommon = flatArr(common);
	const mergeRoute = (routeList) =>
		routeList.map((itm) => {
			const idx = flatCommon.findIndex((it) => it.fullPath === itm.path);
			const commonItem = flatCommon[idx];
			if (commonItem) {
				return {
					...itm,
					component: commonItem.component,
				};
			} else {
				return itm;
			}
		});
	return [...mergeRoute(routeList), ...common];
}
